import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import { farmContactsApi, farmMasterApi, locationsApi, shedMasterApi } from '../../services/resources';
import { formatDate } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import type { FarmContact, FarmMaster, Location, ShedMaster } from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: Farm_Master/Farm_Contacts/Shed_Master/Locations are all
// small, farm-identity-scoped master tables (REQ-001/002) — grouped as tabs on
// one "Farm Setup" page rather than four separate top-level nav items.

const farmConfig: ModuleConfig<FarmMaster> = {
  title: 'Farm Master', entityLabel: 'farm', module: 'System Settings', queryKey: 'farm-master', api: farmMasterApi, idKey: 'id',
  defaultValues: { status: 'Active', timezone: 'Asia/Kolkata' },
  columns: [
    { key: 'code', header: 'Code', primary: true, render: (r) => r.farm_code },
    { key: 'name', header: 'Name', render: (r) => r.farm_name },
    { key: 'address', header: 'Address', render: (r) => r.address, hideOnTablet: true },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_code', label: 'Farm Code', type: 'text', section: 'Details', required: true },
    { name: 'farm_name', label: 'Farm Name', type: 'text', section: 'Details', required: true },
    { name: 'address', label: 'Address', type: 'textarea', section: 'Details', required: true, fullWidth: true },
    { name: 'registration_no', label: 'Registration No.', type: 'text', section: 'Details', optional: true },
    { name: 'timezone', label: 'Timezone', type: 'text', section: 'Details', required: true },
    { name: 'status', label: 'Status', type: 'select', section: 'Details', required: true, staticOptions: [{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }] },
  ],
};

const contactsConfig: ModuleConfig<FarmContact> = {
  title: 'Farm Contacts', entityLabel: 'contact', module: 'System Settings', queryKey: 'farm-contacts', api: farmContactsApi, idKey: 'id',
  referenceKeys: ['farms'], canDelete: true, defaultValues: { is_primary: false },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'type', header: 'Type', render: (r) => r.contact_type },
    { key: 'phone', header: 'Phone', render: (r) => r.phone || '—' },
    { key: 'email', header: 'Email', render: (r) => r.email || '—', hideOnTablet: true },
    { key: 'primary', header: 'Primary', render: (r) => (r.is_primary ? 'Yes' : 'No') },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Contact', required: true, optionsKey: 'farms' },
    { name: 'contact_type', label: 'Contact Type', type: 'select', section: 'Contact', required: true, staticOptions: [
      { value: 'Owner', label: 'Owner' }, { value: 'Veterinarian', label: 'Veterinarian' }, { value: 'FeedSupplier', label: 'Feed Supplier' },
      { value: 'SemenSupplier', label: 'Semen Supplier' }, { value: 'Other', label: 'Other' },
    ] },
    { name: 'name', label: 'Name', type: 'text', section: 'Contact', required: true },
    { name: 'phone', label: 'Phone', type: 'text', section: 'Contact', optional: true },
    { name: 'email', label: 'Email', type: 'text', section: 'Contact', optional: true },
    { name: 'address', label: 'Address', type: 'textarea', section: 'Contact', optional: true, fullWidth: true },
    { name: 'is_primary', label: 'Primary contact for this type', type: 'checkbox', section: 'Contact' },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Contact', optional: true, fullWidth: true },
  ],
};

const shedsConfig: ModuleConfig<ShedMaster> = {
  title: 'Sheds', entityLabel: 'shed', module: 'System Settings', queryKey: 'shed-master', api: shedMasterApi, idKey: 'id',
  referenceKeys: ['farms', 'locations'], defaultValues: { status: 'Active' },
  columns: [
    { key: 'code', header: 'Code', primary: true, render: (r) => r.shed_code },
    { key: 'name', header: 'Name', render: (r) => r.shed_name },
    { key: 'type', header: 'Type', render: (r) => r.shed_type },
    { key: 'occupancy', header: 'Occupancy / Capacity', render: (r) => `${r.current_occupancy ?? 0} / ${r.capacity ?? '—'}` },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Shed Details', required: true, optionsKey: 'farms' },
    { name: 'shed_code', label: 'Shed Code', type: 'text', section: 'Shed Details', required: true },
    { name: 'shed_name', label: 'Shed Name', type: 'text', section: 'Shed Details', required: true },
    { name: 'shed_type', label: 'Type', type: 'select', section: 'Shed Details', required: true, staticOptions: [
      'Cow', 'Calf', 'Sheep', 'Lamb', 'Layer', 'Broiler', 'Quarantine', 'Hospital',
    ].map((v) => ({ value: v, label: v })) },
    { name: 'location_id', label: 'Location', type: 'select', section: 'Shed Details', optional: true, optionsKey: 'locations' },
    { name: 'capacity', label: 'Capacity', type: 'number', section: 'Shed Details', optional: true, min: 0 },
    { name: 'status', label: 'Status', type: 'select', section: 'Shed Details', required: true, staticOptions: [
      { value: 'Active', label: 'Active' }, { value: 'UnderMaintenance', label: 'Under Maintenance' }, { value: 'Inactive', label: 'Inactive' },
    ] },
    { name: 'notes', label: 'Notes', type: 'textarea', section: 'Shed Details', optional: true, fullWidth: true },
  ],
};

const locationsConfig: ModuleConfig<Location> = {
  title: 'Locations', entityLabel: 'location', module: 'System Settings', queryKey: 'locations', api: locationsApi, idKey: 'id',
  referenceKeys: ['farms', 'locations'], canDelete: true, defaultValues: { status: 'Active', location_type: 'Pen' },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'type', header: 'Type', render: (r) => r.location_type },
    { key: 'capacity', header: 'Capacity', render: (r) => r.capacity ?? '—' },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
    { key: 'updated', header: 'Updated', render: (r) => formatDate(r.updated_at), hideOnTablet: true },
  ],
  fields: [
    { name: 'farm_id', label: 'Farm', type: 'select', section: 'Details', required: true, optionsKey: 'farms' },
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'location_type', label: 'Type', type: 'select', section: 'Details', required: true, staticOptions: [
      'Farm', 'Shed', 'Pen', 'Paddock', 'Store', 'Field',
    ].map((v) => ({ value: v, label: v })) },
    { name: 'parent_location_id', label: 'Parent Location', type: 'select', section: 'Details', optional: true, optionsKey: 'locations' },
    { name: 'capacity', label: 'Capacity', type: 'number', section: 'Details', optional: true, min: 0 },
    { name: 'status', label: 'Status', type: 'select', section: 'Details', required: true, staticOptions: [{ value: 'Active', label: 'Active' }, { value: 'Inactive', label: 'Inactive' }] },
  ],
};

export default function FarmSetupPage() {
  return (
    <TabbedPage
      title="Farm Setup"
      subtitle="Farm identity, contacts, sheds and the location hierarchy."
      tabs={[
        { id: 'farm', label: 'Farm Details', render: () => <ResourceModule config={farmConfig} /> },
        { id: 'contacts', label: 'Contacts', render: () => <ResourceModule config={contactsConfig} /> },
        { id: 'sheds', label: 'Sheds', render: () => <ResourceModule config={shedsConfig} /> },
        { id: 'locations', label: 'Locations', render: () => <ResourceModule config={locationsConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Farm Setup', farmConfig, contactsConfig, shedsConfig, locationsConfig);
